/**
 * StaffLogin — pagina login operatore via PIN (R60.59 · Fase 2 · 2026-02-28).
 *
 * UX:
 *  1. Primo setup del tablet: inserimento del codice azienda (6 caratteri).
 *     Il device memorizza l'org_id in localStorage e non lo richiede più.
 *  2. Login quotidiano: griglia dei nomi staff → tocca il tuo nome → tastierino PIN 4 cifre.
 *  3. Pulsante discreto "Rinnega questo tablet" per uscire dal bind (admin).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Delete, KeyRound, Smartphone, UserCheck, X } from "lucide-react";

const LS_KEY = "haccp_staff_bound";

function readBound() {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj?.org_id && obj?.org_name) return obj;
  } catch { /* noop */ }
  return null;
}

export default function StaffLogin() {
  const nav = useNavigate();
  const [bound, setBound] = useState(readBound());
  const [code, setCode] = useState("");
  const [binding, setBinding] = useState(false);
  const [operators, setOperators] = useState([]);
  const [picked, setPicked] = useState(null); // {id, name}
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pinRef = useRef(null);

  useEffect(() => { document.title = "Entra come operatore · e-Log"; }, []);

  // Ricarica operatori se già bound
  useEffect(() => {
    if (!bound?.org_id) return;
    api.get(`/staff/public/${bound.org_id}/operators`).then(({ data }) => {
      setOperators(data.operators || []);
      setBound((b) => ({ ...b, org_name: data.org_name || b.org_name }));
    }).catch((e) => {
      if (e.response?.status === 404) {
        // org non più valida
        unbind();
        toast.error("Azienda non più disponibile. Reinserisci il codice.");
      } else if (e.response?.status === 403) {
        toast.error("Azienda sospesa. Contatta l'amministratore.");
      }
    });
  }, [bound?.org_id]);

  const unbind = () => {
    try { window.localStorage.removeItem(LS_KEY); } catch { /* noop */ }
    setBound(null);
    setOperators([]);
    setPicked(null);
    setPin("");
  };

  const confirmUnbind = () => {
    if (window.confirm("Scollegare questo tablet dall'azienda?\n\nDovrai re-inserire il codice al prossimo utilizzo.")) {
      unbind();
    }
  };

  const submitBind = async (e) => {
    e?.preventDefault?.();
    const c = code.trim().toUpperCase();
    if (!/^[A-Z2-9]{6}$/.test(c)) return toast.error("Codice: 6 caratteri (lettere e numeri).");
    setBinding(true);
    try {
      const { data } = await api.post("/staff/bind", { code: c });
      const info = { org_id: data.org_id, org_name: data.org_name || "", bound_at: new Date().toISOString() };
      try { window.localStorage.setItem(LS_KEY, JSON.stringify(info)); } catch { /* noop */ }
      setBound(info);
      setOperators(data.operators || []);
      toast.success(`Tablet legato a: ${data.org_name || "azienda"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Codice non valido");
    } finally {
      setBinding(false);
    }
  };

  const pickOperator = (op) => {
    setPicked(op);
    setPin("");
    setTimeout(() => pinRef.current?.focus(), 50);
  };

  const typePin = (digit) => {
    if (submitting) return;
    if (pin.length >= 4) return;
    const next = pin + String(digit);
    setPin(next);
    if (next.length === 4) {
      // auto-submit
      setTimeout(() => submitLogin(next), 100);
    }
  };

  const backspace = () => {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
  };

  const submitLogin = async (pinVal) => {
    if (!bound?.org_id || !picked?.id) return;
    const p = (pinVal ?? pin).toString();
    if (p.length !== 4) return;
    setSubmitting(true);
    try {
      await api.post("/staff/login", { org_id: bound.org_id, staff_id: picked.id, pin: p });
      toast.success(`Benvenuto ${picked.name}`);
      // reload pagina per far ri-caricare l'AuthContext
      window.location.href = "/oggi";
    } catch (e) {
      setPin("");
      toast.error(e?.response?.data?.detail || "PIN errato");
      pinRef.current?.focus?.();
    } finally {
      setSubmitting(false);
    }
  };

  // --- UI ---
  if (!bound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#13294B] via-[#1A2F5C] to-[#13294B] text-white flex items-center justify-center px-4 py-10" data-testid="staff-bind-screen">
        <form onSubmit={submitBind} className="w-full max-w-md bg-white text-slate-900 rounded-2xl shadow-2xl p-6 sm:p-7">
          <div className="flex justify-between items-center mb-5">
            <button type="button" onClick={() => nav("/login")} className="text-slate-500 text-sm hover:text-slate-800 inline-flex items-center gap-1" data-testid="back-to-admin-login">
              <ArrowLeft className="w-4 h-4" /> Login admin
            </button>
            <span className="text-[11px] uppercase tracking-widest font-bold text-amber-600 inline-flex items-center gap-1">
              <Smartphone className="w-3.5 h-3.5" /> Setup tablet
            </span>
          </div>
          <h1 className="font-display text-2xl font-bold">Lega questo tablet all'azienda</h1>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            Chiedi all'admin il <b>codice azienda</b> (6 caratteri, lo trova in Team &gt; Operatori con PIN).
            Lo digiti una volta sola: poi lo staff accede toccando il suo nome.
          </p>
          <div className="mt-5">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
              placeholder="ABC123"
              className="h-14 text-center text-3xl tracking-[0.4em] font-mono font-bold"
              autoFocus
              data-testid="staff-bind-code"
            />
          </div>
          <Button
            type="submit"
            disabled={binding || code.length !== 6}
            className="mt-4 w-full h-12 btn-gold text-base font-bold disabled:opacity-60"
            data-testid="staff-bind-submit"
          >
            {binding ? "Verifica…" : "Lega tablet"}
          </Button>
          <p className="mt-4 text-[11px] text-slate-500 text-center">
            Il codice resta uguale per sempre. Puoi scollegare il tablet in qualsiasi momento.
          </p>
        </form>
      </div>
    );
  }

  // Scelta operatore
  if (!picked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#13294B] via-[#1A2F5C] to-[#13294B] text-white flex flex-col items-center justify-center px-4 py-8" data-testid="staff-pick-screen">
        <div className="w-full max-w-2xl">
          <div className="flex items-start justify-between mb-6 gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest font-bold text-amber-300">Entra come operatore</p>
              <h1 className="font-display text-3xl font-bold mt-1">{bound.org_name || "Azienda"}</h1>
              <p className="text-slate-300 text-sm mt-1">Tocca il tuo nome per accedere</p>
            </div>
            <button type="button" onClick={confirmUnbind} className="text-slate-400 text-[11px] hover:text-white inline-flex items-center gap-1 shrink-0" data-testid="staff-unbind-link">
              <X className="w-3 h-3" /> Scollega tablet
            </button>
          </div>

          {operators.length === 0 ? (
            <div className="bg-white/10 border border-white/20 rounded-2xl p-10 text-center" data-testid="staff-empty-ops">
              <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-white/80 text-sm">Nessun operatore configurato per questa azienda.</p>
              <p className="text-white/60 text-xs mt-2">Chiedi all'admin di aggiungere operatori dal pannello Team.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="staff-ops-grid">
              {operators.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  onClick={() => pickOperator(op)}
                  className="bg-white hover:bg-amber-50 text-[#13294B] rounded-2xl p-5 shadow-xl ring-1 ring-black/10 active:scale-[.98] transition-transform"
                  data-testid={`staff-op-${op.id}`}
                >
                  <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-black text-2xl mx-auto">
                    {op.name.charAt(0).toUpperCase()}
                  </div>
                  <p className="mt-3 font-bold truncate text-center">{op.name}</p>
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <button type="button" onClick={() => nav("/login")} className="text-slate-300 text-xs hover:text-white underline underline-offset-2" data-testid="to-admin-login">
              Sei un admin? Login con email
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tastierino PIN
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#13294B] via-[#1A2F5C] to-[#13294B] text-white flex items-center justify-center px-4 py-8" data-testid="staff-pin-screen">
      <div className="w-full max-w-sm bg-white text-slate-900 rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <button type="button" onClick={() => { setPicked(null); setPin(""); }} className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 text-sm" data-testid="pin-back">
            <ArrowLeft className="w-4 h-4" /> Cambia operatore
          </button>
          <span className="text-[11px] uppercase tracking-widest font-bold text-amber-600 inline-flex items-center gap-1">
            <KeyRound className="w-3.5 h-3.5" /> PIN
          </span>
        </div>

        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-black text-2xl mx-auto">
            {picked.name.charAt(0).toUpperCase()}
          </div>
          <p className="mt-2 font-display text-xl font-bold">{picked.name}</p>
          <p className="text-xs text-slate-500">Inserisci il tuo PIN di 4 cifre</p>
        </div>

        {/* display PIN */}
        <div className="flex justify-center gap-3 mb-5" data-testid="pin-display">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-11 h-11 rounded-xl border-2 flex items-center justify-center text-2xl font-bold font-mono ${
                pin.length > i ? "border-amber-500 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-300"
              }`}
            >
              {pin.length > i ? "•" : ""}
            </div>
          ))}
        </div>

        {/* input nascosto (per focus / autofill) */}
        <input
          ref={pinRef}
          type="password"
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          value={pin}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 4);
            setPin(v);
            if (v.length === 4) setTimeout(() => submitLogin(v), 100);
          }}
          className="absolute left-[-9999px]"
          autoFocus
        />

        {/* tastierino */}
        <div className="grid grid-cols-3 gap-2.5" data-testid="pin-pad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => typePin(n)}
              className="h-14 rounded-xl bg-slate-100 hover:bg-slate-200 text-2xl font-bold font-mono active:scale-95 transition disabled:opacity-50"
              disabled={submitting}
              data-testid={`pin-key-${n}`}
            >
              {n}
            </button>
          ))}
          <button type="button" onClick={() => { setPin(""); }} className="h-14 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-600" disabled={submitting} data-testid="pin-clear">
            C
          </button>
          <button type="button" onClick={() => typePin(0)} className="h-14 rounded-xl bg-slate-100 hover:bg-slate-200 text-2xl font-bold font-mono" disabled={submitting} data-testid="pin-key-0">
            0
          </button>
          <button type="button" onClick={backspace} className="h-14 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center" disabled={submitting} data-testid="pin-backspace">
            <Delete className="w-5 h-5" />
          </button>
        </div>

        {submitting && (
          <p className="mt-4 text-sm text-center text-amber-700 font-semibold">Accesso in corso…</p>
        )}
      </div>
    </div>
  );
}
